import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import api from '../api'
import { Plus, HandCoins, PiggyBank } from 'lucide-react'
import { Card, PageHeader, Button, Input, Field, Badge, Avatar, Modal, ModalHeader, Alert, Select } from '../components/ui'

const emptyForm = {
  nama: '', no_wa: '', username: '', password: '', role: 'teknisi',
  tanggal_lahir: '', email: '', tanggal_masuk: '', gender: '', status_kepegawaian: '', jabatan: '', alamat: '',
  no_ktp: '', no_kk: '', no_bpjs_tk: '', no_bpjs_kes: '', nama_bank: '', no_rekening: '',
  gaji_pokok: '', uang_makan_transport: '', potongan_bpjs: ''
}
// Kolom biodata+gaji Fitur Payroll -- dipakai bukaEdit() utk isi ulang form dari data existing
// tanpa nulis nama field manual (sinkron dgn PAYROLL_FIELDS di server/routes/pegawai.js).
// Target pekerjaan SENGAJA TIDAK di sini -- pindah jadi target GLOBAL, lihat halaman Target Kerja.
const PAYROLL_FIELDS = [
  'tanggal_lahir', 'email', 'tanggal_masuk', 'gender', 'status_kepegawaian', 'jabatan', 'alamat',
  'no_ktp', 'no_kk', 'no_bpjs_tk', 'no_bpjs_kes', 'nama_bank', 'no_rekening',
  'gaji_pokok', 'uang_makan_transport', 'potongan_bpjs'
]

function SectionLabel({ children }) {
  return <div style={{ fontSize: 11.5, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 0.3, margin: '18px 0 10px' }}>{children}</div>
}

const row2 = { gap: 12 }

export default function Pegawai() {
  const navigate = useNavigate()
  const [data, setData] = useState([])
  const [modal, setModal] = useState(false)
  const [form, setForm] = useState(emptyForm)
  const [editId, setEditId] = useState(null)
  // Username SUDAH ADA sebelum form ini dibuka (baik pegawai baru yg baru dibikin lewat sini
  // sebelumnya, atau teknisi lama yg sudah dikasih akun) -- kalau iya, field username dikunci
  // (identitas login tidak boleh berubah lewat form edit umum). Kalau belum ada (teknisi lama
  // sebelum fitur ini, username masih NULL), field tetap editable -- ini jalan admin "aktifkan"
  // login pertama kali utk mereka.
  const [usernameSudahAda, setUsernameSudahAda] = useState(false)
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)

  useEffect(() => { load() }, [])

  async function load() {
    const res = await api.get('/pegawai')
    setData(res.data)
  }

  function bukaTambah() {
    setForm(emptyForm)
    setEditId(null)
    setUsernameSudahAda(false)
    setError('')
    setModal(true)
  }

  function bukaEdit(p) {
    const payrollData = {}
    for (const f of PAYROLL_FIELDS) payrollData[f] = p[f] ?? ''
    setForm({ nama: p.nama, no_wa: p.no_wa || '', username: p.username || '', password: '', role: p.role || 'teknisi', ...payrollData })
    setEditId(p.id)
    setUsernameSudahAda(!!p.username)
    setError('')
    setModal(true)
  }

  async function simpan() {
    if (!form.nama.trim()) { setError('Nama wajib diisi'); return }
    if ((form.username && !form.password && !editId) || (form.username && !usernameSudahAda && !form.password)) {
      setError('Password wajib diisi kalau mau bikin akun login (username diisi)'); return
    }
    setLoading(true)
    try {
      if (editId) {
        await api.put(`/pegawai/${editId}`, { ...form, aktif: 1 })
      } else {
        await api.post('/pegawai', form)
      }
      setModal(false)
      load()
    } catch (e) {
      setError(e.response?.data?.message || 'Gagal menyimpan')
    } finally { setLoading(false) }
  }

  const upd = field => e => setForm({ ...form, [field]: e.target.value })

  async function nonaktifkan(id) {
    if (!window.confirm('Nonaktifkan pegawai ini?')) return
    await api.delete(`/pegawai/${id}`)
    load()
  }

  return (
    <div>
      <PageHeader
        title="Kelola Pegawai"
        subtitle={`${data.filter(d => d.aktif).length} pegawai aktif`}
        actions={<Button icon={Plus} onClick={bukaTambah}>Tambah Pegawai</Button>}
      />

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: 16 }}>
        {data.map(p => (
          <Card key={p.id} style={{
            padding: 20, opacity: p.aktif ? 1 : 0.55,
            borderLeft: `3px solid ${p.aktif ? 'var(--brand)' : 'var(--border-strong)'}`
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 14 }}>
              <Avatar label={p.nama.charAt(0).toUpperCase()} size={42} />
              <div style={{ minWidth: 0 }}>
                <div style={{ fontWeight: 600, fontSize: 14.5 }}>{p.nama}</div>
                <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>{p.no_wa || 'No. WA belum diisi'}</div>
              </div>
            </div>
            <div style={{ display: 'flex', gap: 6, alignItems: 'center', marginBottom: 14, flexWrap: 'wrap' }}>
              <Badge tone={p.aktif ? 'success' : 'neutral'}>{p.aktif ? 'Aktif' : 'Nonaktif'}</Badge>
              <Badge tone={p.role === 'admin' ? 'brand' : 'neutral'}>{p.role === 'admin' ? 'Admin' : 'Teknisi'}</Badge>
              {p.username ? <Badge tone="success">Login: {p.username}</Badge> : <Badge tone="neutral">Belum ada akun</Badge>}
            </div>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
              <Button size="sm" variant="soft" icon={PiggyBank} onClick={() => navigate(`/tabungan?teknisi_id=${p.id}`)}>Tabungan</Button>
              <Button size="sm" variant="soft" icon={HandCoins} onClick={() => navigate(`/kasbon?teknisi_id=${p.id}`)}>Kasbon</Button>
              <Button size="sm" variant="soft" onClick={() => bukaEdit(p)}>Edit</Button>
              {p.aktif && <Button size="sm" variant="danger-soft" onClick={() => nonaktifkan(p.id)}>Nonaktifkan</Button>}
            </div>
          </Card>
        ))}
      </div>

      {/* Modal */}
      {modal && (
        <Modal onClose={() => !loading && setModal(false)} width={640}>
          <ModalHeader title={editId ? 'Edit Pegawai' : 'Tambah Pegawai'} onClose={loading ? undefined : () => setModal(false)} />
          {error && <Alert type="error">{error}</Alert>}
          <Field label="Nama Pegawai *" style={{ marginBottom: 14 }}>
            <Input value={form.nama} onChange={e => setForm({ ...form, nama: e.target.value })} placeholder="Nama lengkap pegawai" />
          </Field>
          <div className="grid-2" style={row2}>
            <Field label="Nomor WhatsApp"><Input value={form.no_wa} onChange={e => setForm({ ...form, no_wa: e.target.value })} placeholder="628xxxxxxxxxx" /></Field>
            <Field label="Role *">
              <Select value={form.role} onChange={e => setForm({ ...form, role: e.target.value })}>
                <option value="teknisi">Teknisi</option>
                <option value="admin">Admin</option>
              </Select>
            </Field>
          </div>

          <SectionLabel>Data Pribadi</SectionLabel>
          <div className="grid-2" style={{ ...row2, marginBottom: 12 }}>
            <Field label="Tanggal Lahir"><Input type="date" value={form.tanggal_lahir} onChange={upd('tanggal_lahir')} /></Field>
            <Field label="Gender">
              <Select value={form.gender} onChange={upd('gender')}>
                <option value="">-</option>
                <option value="L">Laki-laki</option>
                <option value="P">Perempuan</option>
              </Select>
            </Field>
          </div>
          <Field label="Email" style={{ marginBottom: 12 }}><Input type="email" value={form.email} onChange={upd('email')} placeholder="nama@email.com" /></Field>
          <Field label="Alamat" style={{ marginBottom: 12 }}><Input value={form.alamat} onChange={upd('alamat')} placeholder="Alamat domisili" /></Field>
          <div className="grid-2" style={row2}>
            <Field label="No. KTP"><Input value={form.no_ktp} onChange={upd('no_ktp')} /></Field>
            <Field label="No. KK"><Input value={form.no_kk} onChange={upd('no_kk')} /></Field>
          </div>

          <SectionLabel>Kepegawaian</SectionLabel>
          <div className="grid-2" style={{ ...row2, marginBottom: 12 }}>
            <Field label="Tanggal Masuk"><Input type="date" value={form.tanggal_masuk} onChange={upd('tanggal_masuk')} /></Field>
            <Field label="Status Kepegawaian">
              <Select value={form.status_kepegawaian} onChange={upd('status_kepegawaian')}>
                <option value="">-</option>
                <option value="tetap">Tetap</option>
                <option value="kontrak">Kontrak</option>
                <option value="harian">Harian Lepas</option>
              </Select>
            </Field>
          </div>
          <Field label="Jabatan"><Input value={form.jabatan} onChange={upd('jabatan')} placeholder="mis. Teknisi Lapangan" /></Field>

          <SectionLabel>Bank & BPJS</SectionLabel>
          <div className="grid-2" style={{ ...row2, marginBottom: 12 }}>
            <Field label="Nama Bank"><Input value={form.nama_bank} onChange={upd('nama_bank')} placeholder="mis. BCA" /></Field>
            <Field label="No. Rekening"><Input value={form.no_rekening} onChange={upd('no_rekening')} /></Field>
          </div>
          <div className="grid-2" style={row2}>
            <Field label="No. BPJS Ketenagakerjaan"><Input value={form.no_bpjs_tk} onChange={upd('no_bpjs_tk')} /></Field>
            <Field label="No. BPJS Kesehatan"><Input value={form.no_bpjs_kes} onChange={upd('no_bpjs_kes')} /></Field>
          </div>

          <SectionLabel>Komponen Gaji Default</SectionLabel>
          <p style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 12 }}>
            Dipakai sbg nilai awal tiap kali admin membuat slip payroll bulanan pegawai ini (bisa disesuaikan per bulan di halaman Payroll).
          </p>
          <div className="grid-2" style={row2}>
            <Field label="Gaji Pokok (Rp)"><Input type="number" value={form.gaji_pokok} onChange={upd('gaji_pokok')} placeholder="0" /></Field>
            <Field label="Makan & Transport (Rp)"><Input type="number" value={form.uang_makan_transport} onChange={upd('uang_makan_transport')} placeholder="0" /></Field>
          </div>
          <Field label="Potongan BPJS Default (Rp)" style={{ marginTop: 12 }}><Input type="number" value={form.potongan_bpjs} onChange={upd('potongan_bpjs')} placeholder="0" /></Field>

          <div style={{ borderTop: '1px solid var(--border)', margin: '18px 0 0', paddingTop: 14 }}>
            <p style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 12 }}>
              Akun login (opsional) — kosongkan kalau pegawai ini cuma dipakai utk assignment nama di pekerjaan/tiket, tidak perlu login ke dashboard.
            </p>
            <Field label={usernameSudahAda ? 'Username (tidak bisa diubah)' : 'Username'} style={{ marginBottom: 14 }}>
              <Input
                value={form.username}
                onChange={e => setForm({ ...form, username: e.target.value })}
                placeholder="mis. budi.teknisi"
                disabled={usernameSudahAda}
              />
            </Field>
            <Field label={editId ? 'Password Baru (kosongkan jika tidak diubah)' : 'Password'} style={{ marginBottom: 8 }}>
              <Input type="password" value={form.password} onChange={e => setForm({ ...form, password: e.target.value })} />
            </Field>
          </div>

          <div style={{ display: 'flex', gap: 10, marginTop: 14 }}>
            <Button variant="neutral" disabled={loading} onClick={() => setModal(false)} style={{ flex: 1, justifyContent: 'center' }}>Batal</Button>
            <Button loading={loading} onClick={simpan} style={{ flex: 1, justifyContent: 'center' }}>{loading ? 'Menyimpan...' : 'Simpan'}</Button>
          </div>
        </Modal>
      )}
    </div>
  )
}
